import React, { useState } from 'react';
import Sidebar from '../Sidebar';
import ModuloHeader from '../ModuloHeader';
import HelpCenter from '../HelpCenter';
import { BookOpen, PiggyBank, ArrowRight, FileText, Image as ImageIcon, Video, Newspaper, Building2 } from 'lucide-react';
import ManualLeitor from './ManualLeitor';
import Videoteca from './Videoteca';
import ConectaAba from './conecta/ConectaAba';
import ManualPortalLeitor from './conecta/ManualPortalLeitor';
import { COR, COR_ESCURA, COR_CLARA } from './BlocosManual';
import manualFinanceiroProcessos from './conteudo/financeiro-processos';

// ---------------------------------------------------------------------------
// MÓDULO BIBLIOTECA
// ---------------------------------------------------------------------------
// O acervo do portal, em duas prateleiras (as ABAS desta tela):
//
//   · MANUAIS   → a documentação de processo do Grupo Correia: o manual longo,
//     ponta a ponta, que antes circulava como PDF no WhatsApp e no e-mail.
//   · VIDEOTECA → os vídeos de procedimento reunidos num lugar só. São os MESMOS
//     que já tocam dentro das telas de trabalho (Petty Cash, Nulos); aqui eles
//     ficam disponíveis para quem quer assistir SEM estar no meio da tarefa.
//
// ⚠️ O MÓDULO NASCEU "MANUAIS" (04/09/2026) E FOI RENOMEADO NO MESMO DIA, ao
//    ganhar a Videoteca: com vídeo dentro, "Manuais" passou a nomear a metade em
//    vez do todo. A rota velha (`/manuais`) segue redirecionando **com a query**
//    — ver o comentário no `App.js` —, porque o link profundo por capítulo é o
//    que se manda no grupo da loja.
//
// ⚠️⚠️ NÃO CONFUNDIR COM O "MANUAL DA TELA" (`src/components/HelpCenter.js`), e a
//    diferença é a razão de os dois existirem:
//      · HelpCenter → ajuda CONTEXTUAL, curta, dentro da tela onde se trabalha.
//        Responde "o que é este campo?", "o que esta aba faz?". Some ao fechar.
//      · Biblioteca → o DOCUMENTO de processo, com objetivo, responsável, passos
//        com print, avisos e checklist de conclusão. Responde "como se faz a
//        sangria total, do começo ao fim". Tem índice, busca e link por capítulo.
//    Um não substitui o outro. Um verbete do HelpCenter do Financeiro que
//    precise dos 6 passos com print está no lugar errado; um capítulo daqui que
//    caiba em 3 linhas sobre um campo também.
//
// ⚠️ POR QUE NÃO É UM PDF NUM `<iframe>`, que seria uma tarde de trabalho: o PDF
//    não tem busca útil (o leitor do navegador acha a palavra, não o capítulo),
//    não tem link para um capítulo, não abre bem no celular da loja — que é onde
//    o gerente de plantão lê — e não deixa a imagem ampliar, o que torna metade
//    dos prints ilegíveis. O conteúdo virou dado (`conteudo/*.js`) e ganhou as
//    quatro coisas de graça.
//
// ⚠️ O ACERVO DE MANUAIS É UMA LISTA EM CÓDIGO, e isso foi decisão (2026-09-04,
//    com a Julia): manual novo é um arquivo em `conteudo/`, revisável em PR,
//    versionado no git. A alternativa — tabela no banco + editor no Painel Admin
//    — daria autonomia ao autor sem passar por deploy, mas custaria schema,
//    migration com GRANT, CRUD, editor de conteúdo rico e upload de imagem: um
//    mini-CMS para manter, para um acervo que hoje tem UM manual. Registrado no
//    PLANO para quando a demanda de autonomia aparecer de verdade.
//    ⚠️ A VIDEOTECA faz o OPOSTO (lê a `dtbVideoProcedimento`) e isso é
//       deliberado: vídeo entra por CLI, sem deploy — ver `Videoteca.js`.
// ---------------------------------------------------------------------------

// O acervo. Cada entrada aponta para o conteúdo e diz de que ÁREA o manual é —
// o que dá o ícone e o agrupamento na estante.
//
// ⚠️ O `id` tem de casar com o do ACERVO em `server/biblioteca/biblioteca-acervo.js`:
//    é dele que sai a pasta das imagens no backend. Divergir faz o manual abrir
//    com todos os prints em "Imagem indisponível" e nada mais.
const MANUAIS = [
    {
        conteudo: manualFinanceiroProcessos,
        area: 'Financeiro',
        icone: PiggyBank,
        descricao:
            'A rotina de caixa do restaurante, do primeiro POS aberto ao fechamento do dia: ' +
            'abertura, sangrias, caixa surpresa, troco, Petty Cash, estorno, nulos e Prosegur.',
    },
];

// As duas prateleiras. A ordem é a do peso: o manual é o conteúdo longo que a
// pessoa vem procurar; o vídeo é o complemento.
const ABAS = [
    { id: 'manuais', label: 'Manuais', icone: BookOpen },
    { id: 'videoteca', label: 'Videoteca', icone: Video },
    { id: 'conecta', label: 'Conecta', icone: Newspaper },
];

const abaValida = (id) => ABAS.some((a) => a.id === id);

const BibliotecaPainel = () => {
    // ⚠️ NÃO HÁ RECORTE POR PERFIL AQUI, e é decisão: quem alcança o módulo vê
    //    todos os manuais do acervo. O portão é a ROTA (`App.js`) e o menu
    //    (`Sidebar.js`), numa lista só. Um segundo filtro por card criaria uma
    //    lista paralela que divergiria da primeira sem sintoma — o manual
    //    sumiria do card de alguém que a rota deixa entrar.
    //    Quando um manual precisar de recorte próprio (um fechamento contábil
    //    que só o `Contabil` deva ver), o lugar é uma chave `perfis` na entrada
    //    do acervo acima, traduzindo o perfil com
    //    `perfilNoModulo(user.perfil, 'biblioteca')` — sempre COM o módulo.
    const [isMobile] = useState(window.innerWidth <= 768);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    // null = estante; senão, o id do manual aberto.
    const [manualAbertoId, setManualAbertoId] = useState(() =>
        // Link direto (`/biblioteca?manual=financeiro-processos&cap=sangria-parcial`)
        // abre o manual já no capítulo — é o que se manda no grupo da loja.
        new URLSearchParams(window.location.search).get('manual')
    );
    // A aba também vive na URL (`?aba=videoteca`), pelo mesmo motivo do link do
    // capítulo: mandar "assiste esse vídeo" tem de cair na Videoteca, não na
    // estante de manuais.
    // ⚠️ `?manual=` MANDA na escolha: um link antigo, feito antes de a Videoteca
    //    existir, não tem `aba` — abrir a Videoteca com um manual pedido faria o
    //    link profundo parecer quebrado.
    const [aba, setAba] = useState(() => {
        const q = new URLSearchParams(window.location.search);
        if (q.get('manual')) return 'manuais';
        const pedida = q.get('aba');
        return abaValida(pedida) ? pedida : 'manuais';
    });

    const trocarAba = (id) => {
        setAba(id);
        const url = new URL(window.location.href);
        // 'manuais' é o padrão: some da URL para o link continuar curto.
        if (id === 'manuais') url.searchParams.delete('aba');
        else url.searchParams.set('aba', id);
        window.history.replaceState(null, '', url);
    };

    const aberto = MANUAIS.find((m) => m.conteudo.id === manualAbertoId);
    // O Manual do Portal é DINÂMICO (id reservado 'portal'): não está no acervo
    // estático `MANUAIS`, é montado da curadoria do Conecta. Ver ManualPortalLeitor.
    const portalAberto = manualAbertoId === 'portal';
    // "Algum leitor aberto" — some as abas e troca o título do cabeçalho.
    const leitorAberto = !!aberto || portalAberto;

    const abrir = (id) => {
        setManualAbertoId(id);
        const url = new URL(window.location.href);
        url.searchParams.set('manual', id);
        window.history.replaceState(null, '', url);
        window.scrollTo({ top: 0 });
    };

    const voltarParaEstante = () => {
        setManualAbertoId(null);
        const url = new URL(window.location.href);
        url.searchParams.delete('manual');
        // O capítulo também sai: senão, abrir outro manual herdaria um `cap` que
        // não existe nele e o leitor cairia no primeiro capítulo sem explicação.
        url.searchParams.delete('cap');
        window.history.replaceState(null, '', url);
        window.scrollTo({ top: 0 });
    };

    // -------------------------------------------------------------------
    // A ESTANTE
    // -------------------------------------------------------------------
    const Estante = () => (
        <div>
            <div style={{
                background: `linear-gradient(135deg, ${COR} 0%, ${COR_ESCURA} 100%)`,
                borderRadius: 16, padding: isMobile ? 24 : 40, color: '#fff',
                marginBottom: 32, position: 'relative', overflow: 'hidden',
                boxShadow: `0 10px 15px -3px ${COR}4d`,
            }}>
                <div style={{ position: 'relative', zIndex: 10 }}>
                    <h2 style={{
                        fontSize: isMobile ? 24 : 32, fontWeight: 800,
                        marginBottom: 12, marginTop: 0, color: '#fff',
                    }}>
                        Manuais de Processo
                    </h2>
                    <p style={{
                        color: '#fde9d3', fontSize: isMobile ? 15 : 18,
                        maxWidth: 650, margin: 0, lineHeight: 1.6,
                    }}>
                        Os procedimentos padronizados do Grupo Correia, com os passos, os prints das
                        telas e o checklist de conclusão de cada rotina.
                    </p>
                </div>
                <div style={{ position: 'absolute', right: -30, top: -40, opacity: 0.12 }}>
                    <BookOpen size={250} color="#ffffff" />
                </div>
            </div>

            {/* ⚠️ COLUNA COM TETO DE LARGURA (`auto-fill` + máximo de 420px), e não
                `auto-fit` com `1fr`: com `1fr`, um acervo de UM manual fazia o card
                único esticar pela largura inteira da tela — num monitor largo ele
                virava um banner de ~1400px com o texto perdido num canto, parecendo
                erro de layout. Com o teto, o card tem sempre tamanho de item de
                estante, e quando o acervo crescer o `auto-fill` enche a linha com
                quantas colunas couberem. No celular é uma coluna cheia, porque lá a
                tela é mais estreita que o mínimo da coluna. */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 420px))',
                gap: 24,
            }}>
                {MANUAIS.map((m) => {
                    const Icone = m.icone;
                    const totalPrints = contarPrints(m.conteudo);
                    return (
                        <div
                            key={m.conteudo.id}
                            onClick={() => abrir(m.conteudo.id)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); abrir(m.conteudo.id); } }}
                            style={{
                                background: '#fff', borderRadius: 16, padding: 24,
                                border: '1px solid #e7e5e4', cursor: 'pointer',
                                display: 'flex', flexDirection: 'column', gap: 14,
                                transition: 'box-shadow 0.15s, transform 0.15s',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = `0 10px 15px -3px ${COR}26`;
                                e.currentTarget.style.transform = 'translateY(-2px)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.transform = 'none';
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{
                                    width: 46, height: 46, borderRadius: 12, background: COR_CLARA,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                    <Icone size={23} style={{ color: COR }} />
                                </div>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{
                                        fontSize: 11, fontWeight: 800, color: '#a8a29e',
                                        textTransform: 'uppercase', letterSpacing: 0.7,
                                    }}>
                                        {m.area}
                                    </div>
                                    <h3 style={{ margin: '2px 0 0', fontSize: 18, fontWeight: 800, color: '#1c1917', lineHeight: 1.25 }}>
                                        {m.conteudo.titulo}
                                    </h3>
                                </div>
                            </div>

                            <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: '#57534e', flex: 1 }}>
                                {m.descricao}
                            </p>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                <Selo icone={FileText} texto={`${m.conteudo.capitulos.length} capítulos`} />
                                <Selo icone={ImageIcon} texto={`${totalPrints} prints`} />
                                <Selo texto={`Versão ${m.conteudo.versao} · ${m.conteudo.ano}`} />
                            </div>

                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 7, paddingTop: 12,
                                borderTop: '1px solid #f5f5f4', color: COR,
                                fontSize: 14, fontWeight: 800,
                            }}>
                                Abrir manual <ArrowRight size={15} />
                            </div>
                        </div>
                    );
                })}

                {/* Manual do Portal — DINÂMICO: montado da curadoria do Conecta
                    (comunicados por módulo), não é um `conteudo/*.js`. Sempre no
                    acervo; se ainda não há comunicados curados, abre num aviso. */}
                <div
                    key="portal"
                    onClick={() => abrir('portal')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); abrir('portal'); } }}
                    style={{
                        background: '#fff', borderRadius: 16, padding: 24,
                        border: `1px solid ${COR}33`, cursor: 'pointer',
                        display: 'flex', flexDirection: 'column', gap: 14,
                        transition: 'box-shadow 0.15s, transform 0.15s',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = `0 10px 15px -3px ${COR}26`;
                        e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.transform = 'none';
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                            width: 46, height: 46, borderRadius: 12, background: COR_CLARA,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                            <Building2 size={23} style={{ color: COR }} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <div style={{
                                fontSize: 11, fontWeight: 800, color: '#a8a29e',
                                textTransform: 'uppercase', letterSpacing: 0.7,
                            }}>
                                Portal · por módulo
                            </div>
                            <h3 style={{ margin: '2px 0 0', fontSize: 18, fontWeight: 800, color: '#1c1917', lineHeight: 1.25 }}>
                                Conecta | Comunicações 
                            </h3>
                        </div>
                    </div>

                    <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: '#57534e', flex: 1 }}>
                        Os comunicados do Conecta que valem como referência do portal, reunidos por
                        módulo — um capítulo por área. Curado na aba Conecta, sempre atual.
                    </p>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        <Selo icone={Newspaper} texto="Vindo do Conecta" />
                        <Selo texto="Sempre atualizado" />
                    </div>

                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 7, paddingTop: 12,
                        borderTop: '1px solid #f5f5f4', color: COR,
                        fontSize: 14, fontWeight: 800,
                    }}>
                        Abrir manual <ArrowRight size={15} />
                    </div>
                </div>
            </div>

            {/* Enquanto o acervo tem um manual só, dizer o que vem a seguir é o
                que evita a pergunta "e o do RH?" chegar como bug. */}
            <p style={{
                marginTop: 28, padding: '14px 18px', borderRadius: 12,
                background: '#fafaf9', border: '1px dashed #d6d3d1',
                fontSize: 13.5, lineHeight: 1.6, color: '#78716c',
            }}>
                Outros manuais entram aqui conforme forem escritos. Para publicar um,
                fale com a equipe de dados — o documento é incorporado ao portal, não anexado
                como PDF, para ganhar índice, busca e link por capítulo.
            </p>
        </div>
    );

    return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f7f6f4' }}>
        <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
        <div style={{
            flex: 1, minWidth: 0,
            padding: isMobile ? '16px' : '24px 32px',
            // ADICIONE ESTAS DUAS LINHAS PARA EMPURRAR O CONTEÚDO:
            marginLeft: isMobile ? '0px' : (isCollapsed ? '100px' : '260px'),
            transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <ModuloHeader
                    eyebrow="Biblioteca"
                    titulo={portalAberto ? 'Manual do Portal Grupo Correia'
                        : aberto ? aberto.conteudo.titulo
                        : aba === 'videoteca' ? 'Videoteca'
                        : aba === 'conecta' ? 'Conecta'
                        : 'Manuais de Processo'}
                    cor={COR}
                    onAbrirManual={() => setIsHelpOpen(true)}
                    onAbrirMenu={() => setIsCollapsed(false)}
                    isMobile={isMobile}
                />

                {/* As abas SOMEM com o manual aberto, de propósito: o leitor é uma
                    sessão de leitura longa, com índice e busca próprios, e ele já
                    tem o "voltar". Trocar de prateleira por baixo de um capítulo
                    aberto seria perder o lugar sem pedir. */}
                {!leitorAberto && (
                    <nav style={{
                        display: 'flex', gap: 4, borderBottom: '2px solid #e7e5e4',
                        marginBottom: 24, marginTop: 8,
                    }}>
                        {ABAS.map(({ id, label, icone: Icone }) => {
                            const ativa = id === aba;
                            return (
                                <button
                                    key={id}
                                    type="button"
                                    onClick={() => trocarAba(id)}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 8,
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        padding: '12px 18px', fontSize: 14, fontFamily: 'inherit',
                                        fontWeight: ativa ? 700 : 500,
                                        color: ativa ? COR : '#78716c',
                                        borderBottom: `3px solid ${ativa ? COR : 'transparent'}`,
                                        marginBottom: -2,
                                    }}
                                >
                                    <Icone size={17} /> {label}
                                </button>
                            );
                        })}
                    </nav>
                )}

                {portalAberto ? (
                    <ManualPortalLeitor
                        isMobile={isMobile}
                        onVoltar={voltarParaEstante}
                    />
                ) : aberto ? (
                    <ManualLeitor
                        manual={aberto.conteudo}
                        onVoltar={voltarParaEstante}
                        isMobile={isMobile}
                    />
                ) : aba === 'videoteca' ? (
                    <Videoteca isMobile={isMobile} />
                ) : aba === 'conecta' ? (
                    <ConectaAba isMobile={isMobile} />
                ) : (
                    <Estante />
                )}
            </div>

            {/* Manual da Tela — verbetes do módulo (HelpCenter › manualBiblioteca).
                ⚠️ `modulo` é obrigatório: sem ele o HelpCenter cai no "junta tudo"
                   e mostra os verbetes de todos os módulos do portal. */}
            <HelpCenter
                isOpen={isHelpOpen}
                onClose={() => setIsHelpOpen(false)}
                modulo="biblioteca"
                secaoInicial="biblioteca-visao-geral"
            />
        </div>
    );
};

/** Quantos prints o manual tem (contando repetição — é o que a pessoa vê). */
function contarPrints(manual) {
    let n = 0;
    manual.capitulos.forEach((c) => {
        (c.blocos || []).forEach((b) => { n += (b.imagens || []).length; });
    });
    return n;
}

function Selo({ icone: Icone, texto }) {
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '4px 9px', borderRadius: 999, background: '#fafaf9',
            border: '1px solid #e7e5e4', fontSize: 12, fontWeight: 700, color: '#57534e',
        }}>
            {Icone && <Icone size={12} style={{ color: '#a8a29e' }} />}
            {texto}
        </span>
    );
}

export default BibliotecaPainel;
